// SidebarNavItems.jsx
import { useState, useContext } from 'react'; // ✅
import { UserContext } from './UserContext';
import { canonicalRole, isAdminType } from './utils/roleUtils';
import { Nav, Badge, Image } from 'react-bootstrap';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { Link, useLocation } from 'react-router-dom';
import { hasStudentNavigation } from './utils/userCompletion';
import openPublicPdf from './utils/openPublicPdf';
import { DISCOVER_ROUTE } from './utils/navRoutes';

import icTicket from './assets/img/ic-note.svg';
import icOnboarding from './assets/img/ic-suitcase.svg';
import icPlatformUpdates from './assets/img/ic-cog-refresh.svg';
import icReporting from './assets/img/ic-graph-rise.svg';
import icDonations from './assets/img/ic-donations.svg';
import icDiscover from './assets/img/ic-discover.svg';
import icJobs from './assets/img/ic-folder-open.svg';
import icSubscriptions from './assets/img/ic-coins.svg';
import icNews from './assets/img/ic-news.svg';
import icSupport from './assets/img/ic-support.svg';
import icProfile from './assets/img/ic-user-circle.svg';
import icCommunities from './assets/img/ic-user-group.svg';

const SidebarNavItems = ({ ticketCount = 0, onNavigate, variant = 'sidebar' }) => {
    const { user } = useContext(UserContext);

    const role = canonicalRole(user?.role);
    const isAdmin = isAdminType(role);
    const isStudent = role === 'student';
    const isDonator = role === 'donatorUser';

    const showStudentNavigation = isStudent && hasStudentNavigation(user);

    const location = useLocation();

    const [openDropdown, setOpenDropdown] = useState(() => {
        if (location.pathname.startsWith('/donations')) return 'donations';
        if (location.pathname.startsWith('/mapping')) return 'mapping';
        if (location.pathname.startsWith('/onboarding')) return 'onboarding';
        if (location.pathname.startsWith('/platform-updates')) return 'updates';
        if (location.pathname.startsWith('/reporting')) return 'reporting';
        return null;
    });

    const toggleDropdown = (key) => setOpenDropdown(openDropdown === key ? null : key);

    const navClass =
        variant === 'sidebar'
            ? 'nav flex-column'          // ✅ desktop matches your current markup
            : 'nav flex-column';         // ✅ force same for offcanvas too

    const submenuClass = 'nav flex-column submenu'; // ✅ match sidebar structure

    return (
        <Nav as="div" className={navClass}>

            {/* ✅ DonatorUser: Donations only */}
            {isDonator ? (
                <>
                    <Nav.Link
                        onClick={() => toggleDropdown('donations')}
                        className={`d-flex justify-content-between align-items-center ${location.pathname.startsWith('/donations') && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icDonations} alt="Donations" title="Donations" />
                            Donations
                        </div>
                        {openDropdown === 'donations' ? <BsChevronUp /> : <BsChevronDown />}
                    </Nav.Link>

                    {openDropdown === 'donations' && (
                        <Nav as="div" className={submenuClass}>
                            <Nav.Link
                                as={Link}
                                to="/donations-my"
                                onClick={onNavigate}
                                className={location.pathname === '/donations-my' && 'active-sub'}
                            >
                                My Donations
                            </Nav.Link>
                            <Nav.Link as={Link} to="/donations-list" onClick={onNavigate} className={location.pathname === '/donations-list' && 'active-sub'}>
                                All Donations
                            </Nav.Link>
                        </Nav>
                    )}
                </>
            ) : showStudentNavigation ? (
                <>

                    {/* Discover */}
                    <Nav.Link
                        as={Link}
                        to={DISCOVER_ROUTE}
                        onClick={onNavigate}
                        className={`d-flex align-items-center ${location.pathname.startsWith('/discovery') && 'active'}`}
                    >
                        <Image src={icDiscover} alt="Discover" title="Discover" />
                        Discover
                    </Nav.Link>

                    {/* Communities */}
                    <Nav.Link
                        as={Link}
                        to="/communities"
                        onClick={onNavigate}
                        className={`d-flex align-items-center ${location.pathname.startsWith('/communities') && 'active'}`}
                    >
                        <Image src={icCommunities} alt="Communities" title="Communities" />
                        Communities
                    </Nav.Link>

                    {/* Jobs & Applications */}
                    <Nav.Link
                        className="d-flex justify-content-between align-items-center disabled"
                        style={{ width: '100%', opacity: 0.5, pointerEvents: 'none', cursor: 'default' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icJobs} alt="Jobs & Applications" title="Jobs & Applications" />
                            Jobs & applications
                        </div>
                        <BsChevronDown />
                    </Nav.Link>

                    {/* Subscriptions */}
                    <Nav.Link
                        className="d-flex align-items-center disabled"
                        style={{ opacity: 0.5, pointerEvents: 'none', cursor: 'default' }}
                    >
                        <Image src={icSubscriptions} alt="Subscriptions" title="Subscriptions" />
                        Subscriptions
                    </Nav.Link>

                    {/* Donations */}
                    <Nav.Link
                        className="d-flex align-items-center disabled"
                        style={{ opacity: 0.5, pointerEvents: 'none', cursor: 'default' }}
                    >
                        <Image src={icDonations} alt="Donations" title="Donations" />
                        Donations
                    </Nav.Link>

                    {/* News */}
                    <Nav.Link
                        className="d-flex justify-content-between align-items-center disabled"
                        style={{ opacity: 0.5, pointerEvents: 'none', cursor: 'default' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icNews} alt="News" title="News" />
                            News
                        </div>
                        <Badge bg="primary" pill>
                            4
                        </Badge>
                    </Nav.Link>

                    {/* Support */}
                    <Nav.Link
                        as={Link}
                        to="/support"
                        onClick={onNavigate}
                        className={`d-flex align-items-center ${location.pathname.startsWith('/support') ? 'active' : ''}`}
                    >
                        <Image src={icSupport} alt="Support" title="Support" />
                        Support
                    </Nav.Link>

                    {/* Profile */}
                    <Nav.Link
                        as={Link}
                        to="/profile"
                        onClick={onNavigate}
                        className={`d-flex align-items-center ${location.pathname.startsWith('/profile') ? 'active' : ''}`}
                    >
                        <Image src={icProfile} alt="Profile" title="Profile" />
                        Profile
                    </Nav.Link>
                    
                </>
            ) : isAdmin ? (
                <>
                    {/* Tickets */}
                    <Nav.Link
                        as={Link}
                        to="/ticket-list"
                        onClick={onNavigate}
                        className={`d-flex justify-content-between align-items-center ${location.pathname === '/ticket-list' && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icTicket} alt="Tickets" title="Tickets" />
                            Tickets
                        </div>
                        <Badge bg="secondary" pill>
                            {ticketCount}
                        </Badge>
                    </Nav.Link>

                    {/* Discover */}
                    <Nav.Link
                        as={Link}
                        to={DISCOVER_ROUTE}
                        onClick={onNavigate}
                        className={`d-flex align-items-center ${location.pathname.startsWith('/discovery') && 'active'}`}
                    >
                        <Image src={icDiscover} alt="Discover" title="Discover" />
                        Discover
                    </Nav.Link>

                    {/* Onboarding */}
                    <Nav.Link
                        onClick={() => toggleDropdown('onboarding')}
                        className={`d-flex justify-content-between align-items-center ${location.pathname.startsWith('/onboarding') && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icOnboarding} alt="Onboarding" title="Onboarding" />
                            Onboarding
                        </div>
                        {openDropdown === 'onboarding' ? <BsChevronUp /> : <BsChevronDown />}
                    </Nav.Link>

                    {openDropdown === 'onboarding' && (
                        <Nav as="div" className={submenuClass}>
                            <Nav.Link as={Link} to="/onboarding-company-list" onClick={onNavigate} className={location.pathname === '/onboarding-company-list' && 'active-sub'}>
                                Companies
                            </Nav.Link>
                            <Nav.Link as={Link} to="/onboarding-user-list" onClick={onNavigate} className={location.pathname === '/onboarding-user-list' && 'active-sub'}>
                                Users
                            </Nav.Link>
                            <Nav.Link as={Link} to="/onboarding-city-list" onClick={onNavigate} className={location.pathname === '/onboarding-city-list' && 'active-sub'}>
                                Cities
                            </Nav.Link>
                            <Nav.Link as={Link} to="/onboarding-country-list" onClick={onNavigate} className={location.pathname === '/onboarding-country-list' && 'active-sub'}>
                                Countries
                            </Nav.Link>
                        </Nav>
                    )}

                    {/* Donations */}
                    <Nav.Link
                        onClick={() => toggleDropdown('donations')}
                        className={`d-flex justify-content-between align-items-center ${location.pathname.startsWith('/donations') && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icDonations} alt="Donations" title="Donations" />
                            Donations
                        </div>
                        {openDropdown === 'donations' ? <BsChevronUp /> : <BsChevronDown />}
                    </Nav.Link>

                    {openDropdown === 'donations' && (
                        <Nav as="div" className={submenuClass}>
                            <Nav.Link as={Link} to="/donations-my" onClick={onNavigate} className={location.pathname === '/donations-my' && 'active-sub'}>
                                My Donations
                            </Nav.Link>
                            <Nav.Link as={Link} to="/donations-list" onClick={onNavigate} className={location.pathname === '/donations-list' && 'active-sub'}>
                                All Donations
                            </Nav.Link>
                        </Nav>
                    )}

                    {/* Platform Updates */}
                    <Nav.Link
                        onClick={() => toggleDropdown('updates')}
                        className={`d-flex justify-content-between align-items-center ${location.pathname.startsWith('/platform-updates') && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icPlatformUpdates} alt="Platform Updates" title="Platform Updates" />
                            Platform Updates
                        </div>
                        {openDropdown === 'updates' ? <BsChevronUp /> : <BsChevronDown />}
                    </Nav.Link>

                    {openDropdown === 'updates' && (
                        <Nav as="div" className={submenuClass}>
                            <Nav.Link
                                href="/Abroadify-Terms-of-Use.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => {
                                    onNavigate?.();
                                    openPublicPdf(event, '/Abroadify-Terms-of-Use.pdf');
                                }}
                                className="nav-link"
                            >
                                Terms of use
                            </Nav.Link>
                            <Nav.Link
                                href="/Abroadify-Privacy-Notice.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => {
                                    onNavigate?.();
                                    openPublicPdf(event, '/Abroadify-Privacy-Notice.pdf');
                                }}
                                className="nav-link"
                            >
                                Privacy Notice
                            </Nav.Link>
                        </Nav>
                    )}

                    {/* Reporting */}
                    <Nav.Link
                        onClick={() => toggleDropdown('reporting')}
                        className={`d-flex justify-content-between align-items-center ${location.pathname.startsWith('/reporting') && 'active'}`}
                        style={{ width: '100%' }}
                    >
                        <div className="d-flex align-items-center">
                            <Image src={icReporting} alt="Reporting" title="Reporting" />
                            Reporting
                        </div>
                        {openDropdown === 'reporting' ? <BsChevronUp /> : <BsChevronDown />}
                    </Nav.Link>

                    {openDropdown === 'reporting' && (
                        <Nav as="div" className={submenuClass}>
                            <Nav.Link as={Link} to="/reporting/jobs" onClick={onNavigate} className={location.pathname === '/reporting/jobs' && 'active-sub'}>
                                Jobs
                            </Nav.Link>
                            <Nav.Link as={Link} to="/reporting/general" onClick={onNavigate} className={location.pathname === '/reporting/general' && 'active-sub'}>
                                General
                            </Nav.Link>
                            <Nav.Link as={Link} to="/reporting/users" onClick={onNavigate} className={location.pathname === '/reporting/users' && 'active-sub'}>
                                Users
                            </Nav.Link>
                            <Nav.Link as={Link} to="/reporting/revenue" onClick={onNavigate} className={location.pathname === '/reporting/revenue' && 'active-sub'}>
                                Revenue
                            </Nav.Link>
                        </Nav>
                    )}
                </>
            ) : null}
        </Nav>
    );
};

export default SidebarNavItems;
